import {FormActionButtons, FormContainer, FormattedFormRow, TextField, useBoolState} from "@dvrt/graphql-widgets";
import Loading from "@/components/breadcrumbs/Loading";
import DomainSettingsRenameForm from "./form";
import {FormattedPageTitle} from "@/components/pages";
import {formPagesWidth} from "@/constants";
import {useDomainNameQuery} from "@/queries.generated";
import {useCallback} from "react";
import ErrorHelper from "@/utils/ErrorHelper";
import {defineMessage} from "react-intl";

export type DomainSettingsRenameProps = {
    domainId: number;
};

const Content = ({
    domainId,
    currentName,
    refetch,
}: {
    domainId: number;
    currentName: string;
    refetch: () => Promise<string>;
}) => {
    const form = new DomainSettingsRenameForm(domainId, currentName);
    const errorHelper = new ErrorHelper(form.errors);

    const [disabled, disable, enable] = useBoolState(false);

    async function submit() {
        disable();
        if (await form.submit()) {
            form.newInitialData({
                domainId,
                newName: await refetch(),
            });
        }
        enable();
    }

    return (
        <div>
            <FormattedPageTitle desc={defineMessage({id: "domainRenamePage.title", defaultMessage: "Domain rename"})} />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow
                    label={defineMessage({id: "domainRenamePage.fields.newName", defaultMessage: "New name"})}
                >
                    <TextField
                        value={form.values.newName}
                        onChange={(value) => form.updateValues({newName: value})}
                        disabled={disabled}
                        errors={errorHelper.fieldErrors("newName")}
                    />
                </FormattedFormRow>
                <FormattedFormRow>
                    <FormActionButtons
                        mainAction={submit}
                        secondaryAction={() => form.reset()}
                        mainButtonEnabled={
                            form.values.newName !== currentName && form.values.newName !== "" && !disabled
                        }
                        secondaryButtonEnabled={form.values.newName !== currentName && !disabled}
                    />
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

const DomainSettingsRename = ({domainId}: DomainSettingsRenameProps) => {
    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
        fetchPolicy: "no-cache",
    });

    const refetch = useCallback(async () => {
        const result = await domainNameQuery.refetch();
        return result.data.domains.items[0].name;
    }, [domainNameQuery]);

    if (domainNameQuery.loading) {
        return <Loading />;
    }

    return <Content domainId={domainId} currentName={domainNameQuery.data.domains.items[0].name} refetch={refetch} />;
};

export default DomainSettingsRename;
